import React, { useEffect, useState } from 'react';
import xrystalLogo from 'assets/images/xrystal-ball.png';
import { StandardLink } from 'components/links/StandardLink';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { handleGetProfile } from 'store/profile';

import { useDispatch } from 'react-redux';
import { handleDestroySession } from 'store/session';
import { FadeOutAndInContainer } from 'components/animations/FadeOutAndInContainer';
import { shuffle } from 'lodash';
import { xrystalQuotes } from 'pages/registration/xrystalQuotes';
import { xrystalDeepOcean, xrystalBrightAzure, xrystalViolet, xrystalLightBluGray, xrystalDarkNavy } from 'core/colors';

interface XrystalUnauthenticatedLayoutProps {
  showSignIn?: boolean;
  children: React.ReactNode;
}

export function XrystalUnauthenticatedLayout(props: XrystalUnauthenticatedLayoutProps) {
  const { children, showSignIn = true } = props;

  const [shuffledQuotes] = useState(shuffle(xrystalQuotes));
  const [quoteIndex, setQuoteIndex] = useState(0);
  const currentQuote = shuffledQuotes[quoteIndex % shuffledQuotes.length];
  const currentUser = useCurrentUser();
  const isSignedIn = !!currentUser;
  const dispatch = useDispatch();

  const handleSignOut = async () => {
    await dispatch(handleDestroySession());
    window.location.reload();
  };

  useEffect(() => {
    dispatch(handleGetProfile());
  }, [dispatch]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setQuoteIndex((c) => c + 1);
    }, 9000);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="w-screen h-screen flex flex-row" style={{ backgroundColor: xrystalLightBluGray }}>
      {/* Left side - Sign in form */}
      <div
        id="xrystal-registration-container-left"
        className="overflow-y-auto lg:w-[500px] flex-grow flex-1 items-center"
        style={{ backgroundColor: 'white' }}
      >
        <div className="registration-header h-[80px] flex-row pl-20">
          <a
            className={`registration-xrystal-logo block flex-row items-end pl-20`}
            href="https://cerego.com"
            target="_self"
            rel="noreferrer"
          >
            <img src={xrystalLogo} alt="Xrystal logo" style={{ width: '72px', height: '72px' }} />
          </a>
        </div>
        <div className="registration-body p-[35px_50px_50px_50px]">{children}</div>
      </div>

      {/* Right side - Crystalline background with gradient and effects */}
      <div
        id="xrystal-registration-container-right"
        className="
              relative
              flex
              w-full
              bg-gradient-to-br
              flex-col
              justify-around
              items-center
              p-4
              min-h-[300px]
              overflow-hidden
              "
        style={{
          background: `linear-gradient(135deg, ${xrystalDeepOcean} 0%, ${xrystalDarkNavy} 50%, ${xrystalViolet} 100%)`,
        }}
      >
        {/* Crystalline overlay effects */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            background: `radial-gradient(circle at 20% 50%, ${xrystalBrightAzure} 0%, transparent 50%), 
                        radial-gradient(circle at 80% 20%, ${xrystalViolet} 0%, transparent 50%),
                        radial-gradient(circle at 40% 80%, ${xrystalBrightAzure} 0%, transparent 50%)`,
          }}
        />

        {/* Animated shimmer effect */}
        <div
          className="absolute inset-0 opacity-10 xrystal-shimmer"
          style={{
            background: `linear-gradient(45deg, transparent 30%, ${xrystalBrightAzure} 50%, transparent 70%)`,
          }}
        />

        {/* Header link area */}
        <div className="registration-header-link flex flex-col justify-end items-end h-[80px] w-full pr-[50px] z-10">
          <nav className="registration-links flex justify-between items-center h-[40px] space-x-5">
            {isSignedIn ? (
              <StandardLink onClick={handleSignOut} className="hover:opacity-80 transition-opacity">
                <span style={{ color: xrystalBrightAzure, textDecoration: 'none' }}>Sign Out</span>
              </StandardLink>
            ) : (
              showSignIn && (
                <StandardLink to={'/signin?brand=xrystal'} className="hover:opacity-80 transition-opacity">
                  <span style={{ color: xrystalBrightAzure, textDecoration: 'none' }}>Sign In</span>
                </StandardLink>
              )
            )}
          </nav>
        </div>

        {/* Quote section with crystalline styling */}
        <div
          className="
                registration-quote
                flex-[1_1_auto]
                font-medium
                text-center
                flex
                flex-col
                justify-center
                items-center
                max-w-[500px]
                z-10
              "
          style={{ color: 'white' }}
        >
          <FadeOutAndInContainer>
            <p className="text-lg leading-relaxed">
              <em style={{ color: xrystalBrightAzure }}>{currentQuote.quote}</em>
              <span className="heading-small block mt-4 font-semibold" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                - {currentQuote.name}
              </span>
            </p>
          </FadeOutAndInContainer>
        </div>

        {/* Bottom crystalline accent */}
        <div
          className="absolute bottom-0 left-0 right-0 h-1"
          style={{
            background: `linear-gradient(90deg, transparent 0%, ${xrystalBrightAzure} 50%, transparent 100%)`,
          }}
        />
      </div>

      <style>
        {`
          @keyframes xrystal-shimmer {
            0% { transform: translateX(-100%); }
            50% { transform: translateX(100%); }
            100% { transform: translateX(100%); }
          }
          .xrystal-shimmer {
            animation: xrystal-shimmer 3s ease-in-out infinite;
          }
        `}
      </style>
    </div>
  );
}
